/*
 * RESPONSIVE UTILS - CHAT-AWARE GRID UTILITIES
 * 
 * Utilities for creating responsive grid layouts that adjust based on chat panel state.
 * 
 * Key Features:
 * - Dynamic grid column generation based on chat state and screen size
 * - Consistent responsive behavior across all card components
 * - Tailwind CSS class string generation
 * - Support for different component types (dashboard, accounts, contacts, my-contacts, notes)
 * - Customizable grid patterns including 4→2→1 layout for my-contacts
 * 
 * Chat-Aware Responsive Grid Matrix:
 * 
 * | Screen Size | Component Type    | Chat Closed | Chat Visible |
 * |-------------|-------------------|-------------|--------------|
 * | Mobile      | All               | 1 column    | 1 column     |
 * | Small       | Dashboard Stats   | 2 columns   | 1 column     |
 * | Small       | Accounts/Contacts | 2 columns   | 1 column     |
 * | Small       | My Contacts       | 2 columns   | 1 column     |
 * | Small       | Notes             | 1 column    | 1 column     |
 * | Medium      | Dashboard Stats   | 3 columns   | 2 columns    |
 * | Medium      | Accounts/Contacts | 3 columns   | 2 columns    |
 * | Medium      | My Contacts       | 2 columns   | 2 columns    |
 * | Medium      | Notes             | 2 columns   | 2 columns    |
 * | Large+      | Dashboard Stats   | 4 columns   | 3 columns    |
 * | Large+      | Accounts/Contacts | 4 columns   | 3 columns    |
 * | Large+      | My Contacts       | 4 columns   | 2 columns    |
 * | Large+      | Notes             | 3 columns   | 2 columns    |
 * 
 * Usage:
 * ```tsx
 * import { getResponsiveGridClasses } from '../utils/responsiveUtils';
 * import { useChatContext } from '../context/ChatContext';
 * 
 * const { isChatVisible } = useChatContext();
 * const gridClasses = getResponsiveGridClasses('accounts', isChatVisible);
 * 
 * return <div className={gridClasses}>...</div>
 * ```
 */

import { ChatState } from '../context/ChatContext';

// Component types for different responsive behaviors
export type ComponentType = 'dashboard' | 'accounts' | 'contacts' | 'my-contacts' | 'notes';

/**
 * Gets responsive grid classes based on component type and chat visibility
 * @param componentType - Type of component ('dashboard' | 'accounts' | 'contacts' | 'my-contacts' | 'notes')
 * @param isChatVisible - Whether chat panel is visible (right-panel state)
 * @returns Tailwind CSS grid classes string
 */
export function getResponsiveGridClasses(
  componentType: ComponentType,
  isChatVisible: boolean
): string {
  const baseClasses = 'grid gap-6';

  switch (componentType) {
    case 'dashboard':
      // Dashboard stats: 4→3, 3→2, 2→1, 1→1
      return isChatVisible
        ? `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3`
        : `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;

    case 'accounts':
    case 'contacts':
      // Accounts/Contacts: 4→3, 3→2, 2→1, 1→1
      return isChatVisible
        ? `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3`
        : `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;

    case 'my-contacts':
      // My Contacts: 4→2, 2→2, 2→1, 1→1 (skipping 3 columns)
      return isChatVisible
        ? `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2`
        : `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4`;

    case 'notes':
      // Notes: 3→2, 2→2, 1→1, 1→1
      return isChatVisible
        ? `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2`
        : `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3`;

    default:
      // Fallback: Conservative responsive grid
      return isChatVisible
        ? `${baseClasses} grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3`
        : `${baseClasses} grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;
  }
}

/**
 * Gets responsive grid classes with custom gap
 * @param componentType - Type of component
 * @param isChatVisible - Whether chat panel is visible
 * @param gap - Custom gap size (e.g., 'gap-4', 'gap-8')
 * @returns Tailwind CSS grid classes string with custom gap
 */
export function getResponsiveGridClassesWithGap(
  componentType: ComponentType,
  isChatVisible: boolean,
  gap: string = 'gap-6'
): string {
  const gridClasses = getResponsiveGridClasses(componentType, isChatVisible);
  return gridClasses.replace('gap-6', gap);
}

/**
 * Determines if chat is visible based on chat state
 * Helper function for components that have access to chatState instead of computed isChatVisible
 * @param chatState - Current chat state
 * @returns Whether chat is visible
 */
export function isChatVisibleFromState(chatState: ChatState): boolean {
  return chatState === 'right-panel';
}

/**
 * Gets grid classes directly from chat state (alternative to isChatVisible)
 * @param componentType - Type of component
 * @param chatState - Current chat state
 * @returns Tailwind CSS grid classes string
 */
export function getResponsiveGridClassesFromState(
  componentType: ComponentType,
  chatState: ChatState
): string {
  const isChatVisible = isChatVisibleFromState(chatState);
  return getResponsiveGridClasses(componentType, isChatVisible);
}

/**
 * Advanced: Gets grid classes with animation-aware transitions
 * Includes transition classes for smooth column adjustments
 * @param componentType - Type of component
 * @param isChatVisible - Whether chat panel is visible
 * @param includeTransitions - Whether to include transition classes
 * @returns Grid classes with optional transition classes
 */
export function getResponsiveGridClassesWithTransitions(
  componentType: ComponentType,
  isChatVisible: boolean,
  includeTransitions: boolean = true
): string {
  const gridClasses = getResponsiveGridClasses(componentType, isChatVisible);

  if (includeTransitions) {
    return `${gridClasses} transition-all duration-300 ease-in-out`;
  }

  return gridClasses;
}
